import TrainingSourceUtils from "@/admin/training-sources/utils/trainingSourceUtils"
import { TrainingSourceSettingsFormFragment$key } from "@/admin/training-sources/__generated__/TrainingSourceSettingsFormFragment.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useFormStore } from "@/core/form/store/FormStore"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import Form from "@components/form/Form"
import { displaySuccessToast } from "@components/toast/ToastProvider"
import {
  DiscoButton,
  DiscoCheckbox,
  DiscoFormControl,
  DiscoInput,
  DiscoLinkInput,
  DiscoRadio,
  DiscoText,
  DiscoTooltip,
} from "@disco-ui"
import DiscoMultiSelect from "@disco-ui/select/DiscoMultiSelect"
import { RadioGroup } from "@material-ui/core"
import { URL_REGEX } from "@utils/string/stringConstants"
import { TestIDProps } from "@utils/typeUtils"
import { IObservableArray } from "mobx"
import { observer } from "mobx-react-lite"
import { useFragment } from "react-relay"
import { ConnectionHandler, graphql } from "relay-runtime"
import {
  TrainingSourceSettingsFormMutation_editMutation,
  WebCrawlScope,
} from "./__generated__/TrainingSourceSettingsFormMutation_editMutation.graphql"
import {
  TrainingSourceKind,
  TrainingSourceSettingsForm__createMutation,
} from "./__generated__/TrainingSourceSettingsForm__createMutation.graphql"

interface TrainingSourceSettingsFormProps extends TestIDProps {
  onSubmit?: () => void
  onClose: () => void
  kind: TrainingSourceKind
  trainingSourceKey?: TrainingSourceSettingsFormFragment$key
}
function TrainingSourceSettingsForm({
  testid,
  onSubmit,
  onClose,
  kind,
  trainingSourceKey,
}: TrainingSourceSettingsFormProps) {
  const classes = useStyles()
  const activeOrganization = useActiveOrganization()!
  const source = useFragment<TrainingSourceSettingsFormFragment$key>(
    graphql`
      fragment TrainingSourceSettingsFormFragment on TrainingSource {
        id
        kind
        url
        name
        resyncPeriod
        metadata {
          web_crawl {
            config {
              scope
              excludeUrlGlobs
            }
          }
        }
      }
    `,
    trainingSourceKey || null
  )

  const createForm = useFormStore<TrainingSourceSettingsForm__createMutation>(
    graphql`
      mutation TrainingSourceSettingsForm__createMutation(
        $input: CreateTrainingSourceInput!
        $connections: [ID!]!
        $kind: TrainingSourceKind!
      ) {
        response: createTrainingSource(input: $input) {
          node
            @appendNode(
              connections: $connections
              edgeTypeName: "TrainingSourceNodeEdge"
            ) {
            id
            organization {
              trainingDataCount(kinds: [$kind])
            }
            ...TrainingSourceListItemFragment
            ...TrainingSourceTableListItemFragment
          }
          errors {
            field
            message
          }
        }
      }
    `,
    {
      organizationId: activeOrganization.id,
      kind,
      name: "",
      url: "",
      resync: true,
      webCrawlConfig: {
        scope: "single_page",
        excludeUrlGlobs: [],
      },
    }
  )

  const { scope, excludeUrlGlobs } = source?.metadata?.web_crawl?.config || {}
  const editForm = useFormStore<TrainingSourceSettingsFormMutation_editMutation>(
    graphql`
      mutation TrainingSourceSettingsFormMutation_editMutation(
        $input: EditTrainingSourceInput!
      ) {
        response: editTrainingSource(input: $input) {
          node {
            ...TrainingSourceListItemFragment
            ...TrainingSourceTableListItemFragment
          }
          errors {
            field
            message
          }
        }
      }
    `,
    {
      id: source?.id || "",
      name: source?.name,
      url: source?.url || "",
      resync: Boolean(source?.resyncPeriod),
      webCrawlConfig: {
        scope,
        excludeUrlGlobs: excludeUrlGlobs || [],
      },
    }
  )

  const form = source?.id ? editForm : createForm

  return (
    <Form
      testid={testid}
      onSubmit={handleSubmit}
      buttons={
        <>
          <DiscoButton onClick={onClose} color={"grey"} variant={"outlined"}>
            {"Cancel"}
          </DiscoButton>
          <Form.SubmitButton form={form}>{"Save"}</Form.SubmitButton>
        </>
      }
    >
      <DiscoFormControl
        label={
          <DiscoText variant={"body-sm"} color={"groovy.neutral.800"}>
            {"Add a public URL"}
          </DiscoText>
        }
        errorMessages={form.errorsByField.url}
      >
        <DiscoLinkInput
          data-testid={testid}
          value={form.state.url!}
          onChange={(v) => (form.state.url = v)}
          placeholder={"yourdomain.com/blog"}
        />
      </DiscoFormControl>

      {renderBody()}
    </Form>
  )

  function renderBody() {
    if (!form.state.url?.length) return null

    return (
      <>
        <DiscoFormControl
          label={
            <DiscoText variant={"body-sm"} color={"groovy.neutral.800"}>
              {"Link Label (Optional)"}
            </DiscoText>
          }
        >
          <DiscoInput
            value={form.state.name}
            onChange={(e) => (form.state.name = e.target.value)}
            placeholder={"Customize link label"}
          />
        </DiscoFormControl>

        {form.state.webCrawlConfig && (
          <>
            <DiscoFormControl
              label={
                <DiscoText variant={"body-sm"} color={"groovy.neutral.800"}>
                  {"Scope"}
                </DiscoText>
              }
            >
              <RadioGroup
                value={form.state.webCrawlConfig.scope}
                onChange={(e) =>
                  (form.state.webCrawlConfig!.scope = e.target.value as WebCrawlScope)
                }
              >
                <DiscoRadio
                  label={TrainingSourceUtils.getWebcrawlScopeLabel("single_page")}
                  value={"single_page"}
                  checked={form.state.webCrawlConfig.scope === "single_page"}
                  variant={"simple"}
                />

                <DiscoRadio
                  label={TrainingSourceUtils.getWebcrawlScopeLabel("domain")}
                  value={"domain"}
                  checked={form.state.webCrawlConfig.scope === "domain"}
                  variant={"simple"}
                />
              </RadioGroup>
            </DiscoFormControl>

            <DiscoFormControl
              label={
                <DiscoText variant={"body-sm"} color={"groovy.neutral.800"}>
                  {"Exclude URLs (Optional)"}
                </DiscoText>
              }
              classes={{
                tooltip: classes.tooltipIcon,
              }}
              tooltip={
                <div className={classes.tooltip}>
                  <DiscoText variant={"body-xs-600"}>
                    {
                      "Add URLs you want to exclude when gathering content. Pages matching these patterns won’t be included. Use * to match any part of the URL. "
                    }
                  </DiscoText>
                  <DiscoText variant={"body-xs-600"}>
                    {
                      "Example: https://yourdomain.com/*/draft skips all pages with /draft."
                    }
                  </DiscoText>
                </div>
              }
            >
              <DiscoMultiSelect
                freeSolo
                testid={testid}
                placeholder={"https://yourdomain.com/*/comments"}
                values={form.state.webCrawlConfig.excludeUrlGlobs as string[]}
                onChange={handleExcludeUrlGlobsChange}
                options={(form.state.webCrawlConfig.excludeUrlGlobs as string[]).map(
                  (value) => ({ value, title: value })
                )}
                filterOptions={{ suggestionMatcher: URL_REGEX }}
                additionalSubmitKeys={[",", " "]}
              />
            </DiscoFormControl>

            <DiscoFormControl>
              <DiscoCheckbox
                checked={form.state.resync || false}
                onChange={() => (form.state.resync = !form.state.resync)}
                label={
                  <DiscoText variant={"body-sm"} color={"groovy.neutral.800"} noWrap>
                    {"Enable auto-resync every week"}

                    <DiscoTooltip
                      buttonProps={{ classes: { root: classes.tooltipButton } }}
                      content={
                        "If you turn off auto-resync, you'll be responsible for manually syncing the link to keep the data up to date."
                      }
                    />
                  </DiscoText>
                }
              />
            </DiscoFormControl>
          </>
        )}
      </>
    )
  }

  function handleExcludeUrlGlobsChange(selections: string[]) {
    ;(form.state.webCrawlConfig?.excludeUrlGlobs as IObservableArray<string>).replace(
      selections
    )
  }

  async function handleSubmit() {
    if (source?.id) {
      await handleEdit()
    } else {
      await handleCreate()
    }
  }

  async function handleEdit() {
    if (!source) return

    const { id: _, ...rest } = editForm.changedState
    const { didSave } = await editForm.submit({
      id: source.id,
      ...rest,
    })
    if (!didSave) return
    onSubmit?.()
  }

  async function handleCreate() {
    const id = ConnectionHandler.getConnectionID(
      activeOrganization.id,
      "TrainingSourceList_trainingSources"
    )
    const trainingLinkId = ConnectionHandler.getConnectionID(
      activeOrganization.id,
      "TrainingSourceTable_trainingSources",
      {
        kinds: [kind],
        search: "",
      }
    )
    const { didSave } = await createForm.submit(createForm.state, {
      connections: [trainingLinkId, id],
      variables: {
        kind,
      },
    })
    if (!didSave) return
    displaySuccessToast({
      message: `Website crawl in progress. This can take a while, check back on this page later!`,
    })
    onSubmit?.()
  }
}

const useStyles = makeUseStyles((theme) => ({
  tooltip: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  tooltipIcon: {
    height: "20px",
    width: "20px",
  },
  tooltipButton: {
    display: "inline",
    width: "20px",
    height: "20px",
    marginLeft: theme.spacing(0.75),
    marginBottom: theme.spacing(0.5),
  },
}))

export default observer(TrainingSourceSettingsForm)
